import { t, useLanguage } from '../../i18n';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, ScrollView, TouchableOpacity, Alert, Linking, Platform, Text } from 'react-native';
import { Button, useTheme, Card, Divider, Chip, ActivityIndicator, List } from 'react-native-paper';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { doc, getDoc, collection, query, where, getDocs, deleteDoc, addDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Course } from '../../types';

const CourseDetailScreen = ({ navigation, route }: any) => {
  const { language } = useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

  const { course } = route.params;
  const courseId = course.courseId;
  const [courseData, setCourseData] = useState<Course | null>(course);
  const [loading, setLoading] = useState(true);
  const [userCourseId, setUserCourseId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);

  const theme = useTheme();

  useEffect(() => {
    fetchDetailData();
  }, [courseId]);

  const fetchDetailData = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      // Check if course is in user's schedule
      const userCoursesRef = collection(db, 'users', auth.currentUser.uid, 'courses');
      const userCoursesQuery = query(
        userCoursesRef,
        where('courseId', '==', courseId)
      );

      const userCoursesSnapshot = await getDocs(userCoursesQuery);
      if (!userCoursesSnapshot.empty) {
        setUserCourseId(userCoursesSnapshot.docs[0].id);
      }

      // Get assignments for this course
      const assignmentsRef = collection(db, 'assignments');
      const assignmentsQuery = query(
        assignmentsRef,
        where('courseId', '==', courseId),
        where('userId', '==', auth.currentUser.uid)
      );

      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAssignments(assignmentsData);
    } catch (error) {
      console.error('Error fetching course detail data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSchedule = async () => {
    if (!auth.currentUser || !courseData) return;

    try {
      if (userCourseId) {
        // Remove from schedule
        await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'courses', userCourseId));
        setUserCourseId(null);
        Alert.alert(t("成功"), t("時間割から削除されました"));
      } else {
        // Add to schedule
        const userCourseRef = await addDoc(
          collection(db, 'users', auth.currentUser.uid, 'courses'),
          {
            ...courseData,
            addedAt: new Date(),
          }
        );
        setUserCourseId(userCourseRef.id);
        Alert.alert(t("成功"), t("時間割に追加されました"));
      }
    } catch (error) {
      console.error('Error toggling course in schedule:', error);
      Alert.alert(t("エラー"), t("操作に失敗しました"));
    }
  };

  const dayLabels = ['', '月', '火', '水', '木', '金', '土', '日'];

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: theme.colors.primary }]}>{t("授業詳細")}</Text>
        <TouchableOpacity onPress={toggleTheme}>
          <Ionicons name={isDarkMode ? 'sunny' : 'moon'} size={24} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Card style={styles.mainCard}>
          <Card.Content>
            <View style={styles.courseHeader}>
              <Text style={[styles.courseName, { color: theme.colors.onSurface }]}>{courseData?.name}</Text>
              <Chip style={[styles.categoryChip, { backgroundColor: theme.colors.surfaceVariant }]} textStyle={{ color: theme.colors.onSurfaceVariant }}>{courseData?.faculty}</Chip>
            </View>
            <Text style={[styles.professorName, { color: theme.colors.onSurfaceVariant }]}>{courseData?.professor} {t("教授")}</Text>

            <View style={styles.gridInfo}>
              <View style={styles.infoItem}>
                <Ionicons name="time-outline" size={16} color={theme.colors.primary} />
                <Text style={[styles.infoText, { color: theme.colors.onSurface }]}>
                  {courseData?.dayOfWeek && courseData?.period ? t('{0}曜{1}限', [t(dayLabels[courseData.dayOfWeek]), courseData.period]) : t('不定期')}
                </Text>
              </View>
              <View style={styles.infoItem}>
                <Ionicons name="location-outline" size={16} color={theme.colors.primary} />
                <Text style={[styles.infoText, { color: theme.colors.onSurface }]}>{courseData?.room || t("未定")}</Text>
              </View>
              <View style={styles.infoItem}>
                <Ionicons name="school-outline" size={16} color={theme.colors.primary} />
                <Text style={[styles.infoText, { color: theme.colors.onSurface }]}>{courseData?.credits} {t("単位")}</Text>
              </View>
            </View>

            <Button
              mode={userCourseId ? "outlined" : "contained"}
              onPress={handleToggleSchedule}
              style={styles.actionButton}
              icon={userCourseId ? "minus" : "plus"}
            >
              {userCourseId ? t("時間割から削除") : t("時間割に追加")}
            </Button>
          </Card.Content>
        </Card>

        {courseData?.syllabusUrl && (
          <Button
            mode="text"
            onPress={() => Linking.openURL(courseData.syllabusUrl!)}
            style={styles.syllabusButton}
          >
            {t("シラバスを表示（外部サイト）")}
          </Button>
        )}

        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("課題・メモ")}</Text>
          {loading ? (
            <ActivityIndicator style={styles.loader} />
          ) : assignments.length === 0 ? (
            <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("登録されている課題はありません")}</Text>
          ) : (
            assignments.map((item) => (
              <List.Item
                key={item.id}
                title={item.title}
                titleStyle={{ color: theme.colors.onSurface }}
                description={item.dueDate ? t("期限: {0}", [new Date(item.dueDate.toDate()).toLocaleDateString(language === 'en' ? 'en-US' : 'ja-JP')]) : t("期限なし")}
                descriptionStyle={{ color: theme.colors.onSurfaceVariant }}
                left={props => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="clipboard-text-outline" size={24} color={theme.colors.onSurfaceVariant} />} />}
                onPress={() => navigation.navigate('AssignmentDetail', { assignmentId: item.id })}
                style={[styles.assignmentItem, { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }]}
              />
            ))
          )}
          <Button
            mode="outlined"
            onPress={() => navigation.navigate('AddAssignment', { courseId })}
            style={styles.addAssignmentButton}
          >
            {t("課題を追加")}
          </Button>
        </View>

        {courseData?.notes && (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("備考")}</Text>
            <Text style={[styles.notesText, { color: theme.colors.onSurfaceVariant }]}>{courseData.notes}</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 40,
  },
  mainCard: {
    marginBottom: 16,
    elevation: 2,
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  courseName: {
    fontSize: 22,
    fontWeight: 'bold',
    flex: 1,
  },
  categoryChip: {
    marginLeft: 8,
  },
  professorName: {
    fontSize: 16,
    marginBottom: 16,
  },
  gridInfo: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '50%',
    marginBottom: 12,
  },
  infoText: {
    marginLeft: 8,
    fontSize: 14,
  },
  actionButton: {
    marginTop: 8,
  },
  syllabusButton: {
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  emptyText: {
    fontStyle: 'italic',
    marginBottom: 12,
  },
  assignmentItem: {
    borderRadius: 8,
    marginBottom: 8,
  },
  addAssignmentButton: {
    marginTop: 8,
  },
  notesText: {
    fontSize: 14,
    lineHeight: 20,
  },
  loader: {
    marginVertical: 20,
  },
});

export default CourseDetailScreen;
